import * as React from 'react';

import type TimeCardEntryVM from 'ab-viewModels/timeSheet/timeCardEntry.viewModel';
import type TimeCardRejectedTimeSheetVM from 'ab-viewModels/timeSheet/timeCardRejectedTimeSheet.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';
import type * as TimeSheetEntryUtils from '@acceligentllc/shared/utils/timeSheetEntry';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import DayInTimeCard from './DayInTimeCard';
import styles from './styles.module.scss';

interface RejectedByDate {
	rejected: TimeCardRejectedTimeSheetVM[];
}

interface OwnProps {
	allAccountEntries: TimeCardEntryVM[];
	hasUnassignedEntries: number;
	hasUnassignedEntriesForMessage: number;
	rejectedTSForAccount: TimeCardRejectedTimeSheetVM[];
	overlaps: Nullable<Record<string, TimeSheetEntryUtils.OverlapMeta>>;
	timeOffset: number;
	timeZoneInUse: Nullable<string>;
}

type Props = OwnProps;

const TimeCard: React.FC<Props> = (props: Props) => {
	const {
		hasUnassignedEntriesForMessage,
		allAccountEntries,
		rejectedTSForAccount,
		overlaps,
		timeOffset,
		timeZoneInUse,
	} = props;

	const canSignRef = React.useRef<Nullable<boolean>>(null);
	const iconName = !canSignRef.current ? 'charm_circle_check' : null;

	const entriesGroupedByDate = React.useMemo(() => {
		const canSignByWorkOrderId: Record<string, boolean> = {};

		// Initializing today's date to empty since it must be shown
		// All account entries are already sorted so to avoid double iteration keeping map instead of sorted array
		const entries = allAccountEntries.reduce<{ [date: string]: TimeCardEntryVM[]; }>((acc, _tle) => {
			const tseStartDate = TimeUtils.formatDate(_tle.startTime, TimeFormat.DATE_ONLY, TimeFormat.ISO_DATETIME);

			if (!!_tle.workOrderId && canSignByWorkOrderId[_tle.workOrderId] !== false) {
				canSignByWorkOrderId[_tle.workOrderId] = !_tle.isSigned && !_tle.isInActiveShift;
			}

			if (!acc[tseStartDate]) {
				acc[tseStartDate] = [];
			}

			acc[tseStartDate].push(_tle);

			return acc;
		}, { [TimeUtils.formatDate(new Date(), TimeFormat.DATE_ONLY)]: [] });

		canSignRef.current = Object.values(canSignByWorkOrderId).some((canSign) => canSign);

		return entries;
	}, [allAccountEntries]);

	const rejectedTSForDay = React.useMemo(() => {
		return rejectedTSForAccount.reduce<Record<string, RejectedByDate>>((acc, _rejected) => {
			const dueDate = TimeUtils.formatDate(_rejected.dueDate, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY);

			if (!acc[dueDate]) {
				acc[dueDate] = { rejected: [] };
			}

			acc[dueDate].rejected.push(_rejected);

			return acc;
		}, {});
	}, [rejectedTSForAccount]);

	const renderLogos = React.useCallback(() => {
		return (
			<div className="icon-acceligent text-orange" />
		);
	}, []);

	const isInActiveShift: boolean = React.useMemo(() => { return allAccountEntries.some((_tse) => _tse.isInActiveShift); }, [allAccountEntries]);

	return (
		<div className={styles.timeCard}>
			{/* TIME CARD HEADER */}
			<div className={styles.timeCardHeader}>
				<div className={styles.headerIcon}>
					{renderLogos()}
				</div>
				<div className={styles.timeCardName}>
					Time Card
				</div>
				<div className={styles.headerSettingsIcon}>
					<span className="icon-actions icon" />
				</div>
			</div>

			{/* TIME CARD BODY */}
			{!!rejectedTSForAccount.length && <div className={styles.timeCardEntriesAlert}>
				{(rejectedTSForAccount.length > 1) ?
					'Multiple Time Sheets have been rejected.'
					:
					<div>
						Time Sheet for <b>{rejectedTSForAccount[0].workOrderCode}</b> of <b>{rejectedTSForAccount[0].dueDate}</b> has been rejected by {rejectedTSForAccount[0].rejectedByFullName ?? 'Acceligent Customer Service'}.
					</div>
				}
			</div>}

			{overlaps && Object.keys(overlaps).length !== 0 && <div className={styles.timeCardEntriesAlert}>
				One or more corrections overlap with activities on the timeline.
			</div>}
			{/* If there is overlap on time card we don't show error message for unassigned entries */}
			{!!overlaps && !!hasUnassignedEntriesForMessage && <div className={styles.timeCardEntriesAlert}>
				There are Time Entires which are not assigned to a Work Order.
			</div>}
			{isInActiveShift && <div className={styles.timeCardEntriesWarning}>
				You have an active shift. <b>Go to active shift. {'>'} </b>
			</div>}

			{/* eslint-disable-next-line @typescript-eslint/prefer-optional-chain */}
			{entriesGroupedByDate && Object.entries(entriesGroupedByDate).map(([_date, _entries]) => {
				return (
					<React.Fragment key={_date}>
						<DayInTimeCard
							date={_date}
							entries={_entries}
							overlaps={overlaps}
							rejectedTS={rejectedTSForDay[_date]?.rejected ?? []}
							timeOffset={timeOffset}
							timeZoneInUse={timeZoneInUse}
						/>
					</React.Fragment>
				);
			})}

			{/* EMPTY SPACE */}
			<div className={styles.emptySpace}></div>

			{/* TIME CARD BUTTONS */}
			<div className={styles.footerButtons}>
				{!isInActiveShift && <div className={styles.timeCardButton} >
					Start New Shift
				</div>}
				{canSignRef.current ?
					<>
						<div className={styles.signTCButton}>
							Sign Time Card
						</div>
					</>
					:
					<>
						<div className={!isInActiveShift ? styles.signedButton : styles.signedButtonRightPositioned}>
							{iconName && <span className="icon-charm_circle_check" />} Signed
						</div>
					</>
				}
			</div>
		</div >
	);
};

export default React.memo(TimeCard);
